import React, { FC } from 'react';
import classnames from 'classnames';

import './CircleIconRebrand.css';

export interface CircleIconPropsType
  extends React.DetailedHTMLProps<React.ImgHTMLAttributes<HTMLImageElement>, HTMLImageElement> {
  /**
   * Alt text for img element
   */
  alt?: string;
  /**
   * Custom CSS classes for the element
   */
  className?: string;
  /**
   * The SVG image to render.
   */
  image: string;
  /**
   * Desired height/width, in pixels
   */
  size?: number;
  /**
   * Specify a variant background color
   */
  variant?: 'cream' | 'white' | 'transparent';
  /**
   * Style prop for overring background color
   */
  style?: { [backgroundColor: string]: string };
}

export type ColorCircleIconPropsType = Omit<CircleIconPropsType, 'variant'>;

export interface CustomCircleIconPropsType extends Omit<CircleIconPropsType, 'variant'> {
  bgColor: string;
}

/**
 * Renders an image styled within a circle. Used primarily (but not exclusively) with the illustrated icons exported from `src/assets/images/icons`
 * Can be rendered with cream background (default), white (equivalent to `<WhiteCircleIcon />`) or no background (equivalent to `<ClearCircleIcon />`).
 *
 * `import { CircleIcon } from '@zola/zola-ui/src/components/CircleIcon';`
 *
 * variants:
 *
 * `import { CreamCircleIcon } from '@zola/zola-ui/src/components/CircleIcon';` <-- equivalent to `<CircleIcon />`
 * `import { WhiteCircleIcon } from '@zola/zola-ui/src/components/CircleIcon';`
 * `import { ClearCircleIcon } from '@zola/zola-ui/src/components/CircleIcon';`
 * `import { CustomCircleIcon } from '@zola/zola-ui/src/components/CircleIcon';` <-- requires a bgColor prop
 */
const CircleIcon: FC<CircleIconPropsType> = ({
  alt = '',
  className,
  image,
  size = 50,
  variant = 'cream',
  ...rest
}) => {
  const classes = classnames('zola-ui', 'circle-icon', className, variant);
  const defaults = {
    height: size,
    width: size,
  };

  const imageProps = Object.assign({}, defaults, rest);
  return <img className={classes} src={image} alt={alt} {...imageProps} />;
};

export const WhiteCircleIcon: FC<ColorCircleIconPropsType> = (props) => (
  <CircleIcon variant="white" {...props} />
);

export const CreamCircleIcon: FC<ColorCircleIconPropsType> = (props) => (
  <CircleIcon variant="cream" {...props} />
);

export const ClearCircleIcon: FC<ColorCircleIconPropsType> = (props) => (
  <CircleIcon variant="transparent" {...props} />
);

export const CustomCircleIcon: FC<CustomCircleIconPropsType> = ({ bgColor, ...rest }) => (
  <CircleIcon
    variant="transparent"
    {...rest}
    style={{
      backgroundColor: bgColor,
    }}
  />
);

export default CircleIcon;
